import * as THREE from "../../libs/three.js/build/three.module.js";

/**
 * PolygonTransfer — 多边形棱柱的独立文件导出/导入（JSON 与 GeoJSON）。
 *
 * 与工程保存（SaveProject.createVolumeData）同一数据形状，区别是这里面向
 * 单个棱柱文件：跨工程复用 / 交付给第三方。纯逻辑（build/parse）与 DOM
 * （下载、file input）分离——本模块只做前者，面板负责接线。
 *
 * JSON 格式（formatVersion 1）：
 *   { formatVersion, type:"YDPolygonVolume", name, position, rotation, scale,
 *     clip, columnMode, points:[[x,y,z]×n（局部坐标）], zMin, zMax（局部，原点=Z中点） }
 *   columnMode：true=柱状（平底平顶取最高/最低Z），false/缺省=连线（底环逐点 Z）
 *
 * GeoJSON：Feature/Polygon，环 = 世界坐标 [x, y, 世界Z] 首尾闭合；
 * properties 携带 name/zMin/zMax（世界高度）/clip/columnMode 供导入还原。
 */

export const POLYGON_JSON_FORMAT_VERSION = 1;

/** 从 volume 实例构造可序列化的 JSON 对象（不 stringify，不触 DOM） */
export function buildVolumeJSON(volume) {
	return {
		formatVersion: POLYGON_JSON_FORMAT_VERSION,
		type: 'YDPolygonVolume',
		name: volume.name,
		position: volume.position.toArray(),
		rotation: volume.rotation.toArray(),
		scale: volume.scale.toArray(),
		clip: volume.clip,
		columnMode: volume.columnMode === true,
		points: volume.points.map(p => p.toArray()),
		zMin: volume.zMin,
		zMax: volume.zMax,
	};
}

/**
 * 解析 JSON 数据包为可赋给 YDPolygonVolume 的字段（校验失败抛错）。
 * zMin/zMax 缺失时从 points 的 Z 推导；zMax<zMin 自动对调。
 */
export function volumeFromJSON(data) {
	if (!data || !Array.isArray(data.points) || data.points.length < 3) {
		throw new Error('points 需为 ≥3 个 [x,y,z] 数组');
	}
	const points = data.points.map((p, i) => {
		if (!Array.isArray(p) || p.length < 2 ||
			p.some(v => typeof v !== 'number' || Number.isNaN(v))) {
			throw new Error(`points[${i}] 含非法坐标`);
		}
		return new THREE.Vector3(p[0], p[1], p.length > 2 ? p[2] : 0);
	});

	let zMin = Number.isFinite(data.zMin) ? data.zMin : Math.min(...points.map(p => p.z));
	let zMax = Number.isFinite(data.zMax) ? data.zMax : Math.max(...points.map(p => p.z));
	if (zMax < zMin) {
		[zMin, zMax] = [zMax, zMin];
	}

	return {
		name: typeof data.name === 'string' && data.name ? data.name : undefined,
		position: Array.isArray(data.position) ? data.position : [0, 0, 0],
		rotation: Array.isArray(data.rotation) ? data.rotation : [0, 0, 0],
		scale: Array.isArray(data.scale) ? data.scale : [1, 1, 1],
		clip: !!data.clip,
		// 旧文件无 columnMode 字段：true（柱状）即历史行为
		columnMode: data.columnMode !== undefined ? !!data.columnMode : true,
		points,
		zMin,
		zMax,
	};
}

/** 从 volume 实例构造 GeoJSON Feature（环用世界坐标，首尾闭合） */
export function buildVolumeGeoJSON(volume) {
	const world = volume.getWorldPoints();
	if (world.length < 3) {
		throw new Error('多边形顶点不足 3 个，无法导出');
	}
	const ring = world.map(p => [p.x, p.y, p.z]);
	ring.push([world[0].x, world[0].y, world[0].z]); // GeoJSON 环必须首尾闭合

	return {
		type: 'Feature',
		geometry: {
			type: 'Polygon',
			coordinates: [ring],
		},
		properties: {
			name: volume.name,
			zMin: volume.position.z + volume.zMin,
			zMax: volume.position.z + volume.zMax,
			clip: volume.clip,
			columnMode: volume.columnMode === true,
			coordinateOrder: 'x,y,z=worldZ',
		},
	};
}

/**
 * 解析 GeoJSON（Feature 或裸 geometry），返回 { ring, properties }：
 * ring 为世界坐标 THREE.Vector3 数组（已去掉重复闭合点），供
 * volume.setFromWorldPoints 使用——局部数据/原点居中由它派生。
 */
export function volumeFromGeoJSON(geojson) {
	const geom = geojson && geojson.geometry ? geojson.geometry : geojson;
	if (!geom || geom.type !== 'Polygon' || !Array.isArray(geom.coordinates) || geom.coordinates.length === 0) {
		throw new Error('需要 GeoJSON Polygon（Feature 或裸 geometry）');
	}

	const raw = geom.coordinates[0];
	const ring = raw.map(p => new THREE.Vector3(
		p[0],
		p[1],
		p.length > 2 && Number.isFinite(p[2]) ? p[2] : 0,
	));
	// 去掉首尾重复的闭合点
	if (ring.length > 1) {
		const first = ring[0];
		const last = ring[ring.length - 1];
		if (first.x === last.x && first.y === last.y && first.z === last.z) {
			ring.pop();
		}
	}
	if (ring.length < 3) {
		throw new Error('环至少需要 3 个不重复点');
	}

	const properties = geojson && geojson.properties ? geojson.properties : {};
	return { ring, properties };
}
